import React, { useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, Tooltip as RechartsTooltip } from 'recharts';
import { motion } from 'framer-motion';
import { 
  FiSun, FiBattery, FiGrid, FiDollarSign, FiSettings, 
  FiActivity, FiInfo, FiTrendingUp, FiTrendingDown
} from 'react-icons/fi';
import { 
  RiMoneyDollarCircleLine, RiLineChartLine, RiDashboardLine, RiWindyLine
} from 'react-icons/ri';

// Import components
import MetricCard from './ui/MetricCard';
// Tooltip component is used in the UI elements
// import Tooltip from './ui/Tooltip';
import TabNav from './ui/TabNav';
import InfoCard from './ui/InfoCard';
import NumericInput from './ui/NumericInput';
import SummaryTable from './ui/SummaryTable';

// Import context providers and hooks
import { useConfig, ConfigProvider } from '../context/ConfigContext';
import { useParams, ParamsProvider } from '../context/ParamsContext';
import { useCalculations } from '../hooks/useCalculations';

// Import utility functions
import { formatCurrency, formatPercentage } from '../utils/formatters';

/**
 * Main DynamicDashboard component that integrates context providers
 */
const DynamicDashboardWrapper: React.FC = () => {
  return (
    <ConfigProvider>
      <ParamsProvider>
        <DynamicDashboardContent />
      </ParamsProvider>
    </ConfigProvider>
  );
};

/**
 * Inner component that uses the context values
 */
const DynamicDashboardContent: React.FC = () => {
  // State for active tab
  const [activeTab, setActiveTab] = useState<string>('inputs');
  
  // Get values from context
  const { config, operatingScenario, setOperatingScenario, updateConfig } = useConfig();
  const { params, updateParam, updateFeedInRate } = useParams();
  
  // Monthly exports data is managed in the ParamsContext
  
  // Get calculations from custom hook
  const {
    npvResults,
    breakeven,
    irr,
    batteryLifespanYears,
    dailyPowerFlow,
    energyMix,
    carbonEmissions,
    combinedChartData,
    cumulativeCostData,
    lifecycleCostsTable
  } = useCalculations(config, params, operatingScenario);
  
  // Derived values
  const totalSystemCost = params.solarCost + params.batteryCost;
  const generatorHourlyCost = 2 * params.generatorConsumption * params.generatorFuelCost;
  
  // This calculation has been moved to useCalculations hook
  /* const calculatePresentValue = (futureValue: number, years: number): number => {
    return futureValue / Math.pow(1 + params.discountRate, years);
  }; */
  
  // This function has been moved to useCalculations hook
  /* const calculateYearlyCostsPerKwh = (scenario: 'current' | 'expanded') => {
    const currentConfig = config[scenario];
    const years = Array.from({ length: params.batteryAnalysisYears + 1 }, (_, i) => i);
    
    // 100% Grid
    const gridOnlyCostsPerKwh = years.map(year => {
      if (year === 0) return null;
      
      // Annual costs in nominal terms
      const annualCost = currentConfig.dailyConsumption * params.gridRate * 1000 * 365;
      
      // Discount to present value - used in cumulative calculation below
      
      // Cumulative PV costs
      const cumulativePVCost = Array.from({ length: year }, (_, index) => 
        calculatePresentValue(annualCost, index)
      ).reduce((sum, cost) => sum + cost, 0);
      
      // Total energy over the period
      const totalKwh = year * (currentConfig.dailyConsumption * 1000 * 365);
      
      // Levelized cost
      const levelizedCost = cumulativePVCost / totalKwh;
      
      return { 
        year, 
        value: levelizedCost,
        nominalValue: params.gridRate,
        annualCost: annualCost,
        cumulativeCost: annualCost * year
      };
    }).filter(item => item !== null);
    
    // Grid-Tied Solar
    const gridTiedCostsPerKwh = years.map(year => {
      if (year === 0) return null;
      
      const initialInvestment = params.solarCost + params.connectionCost;
      
      // Annual operational costs
      const annualGridCost = (currentConfig.gridNeeded * params.gridRate * 1000 * 365);
      const annualFeedInCredit = (currentConfig.excessSolar * params.feedInEffectiveRate * 1000 * 365);
      const annualOperatingCost = annualGridCost - annualFeedInCredit;
      
      // Present value calculation handled in cumulative calculation below
      
      // Cumulative PV costs
      const cumulativePVOperatingCost = Array.from({ length: year }, (_, i) => 
        calculatePresentValue(annualOperatingCost, i)
      ).reduce((sum, cost) => sum + cost, 0);
      
      const totalPVCost = initialInvestment + cumulativePVOperatingCost;
      
      // Total energy over the period
      const totalKwh = year * (currentConfig.dailyConsumption * 1000 * 365);
      
      // Levelized cost
      const levelizedCost = totalPVCost / totalKwh;
      
      return { 
        year, 
        value: levelizedCost,
        nominalValue: annualOperatingCost / (currentConfig.dailyConsumption * 1000 * 365),
        annualCost: annualOperatingCost,
        cumulativeCost: initialInvestment + (annualOperatingCost * year)
      };
    }).filter(item => item !== null);
    
    // Solar + Battery
    const batteryCostsPerKwh = years.map(year => {
      if (year === 0) return null;
      
      const initialInvestment = totalSystemCost + params.connectionCost;
      
      // Calculate maintenance costs
      const maintenanceYears = params.batteryMaintenanceYears.filter(y => y <= year);
      const maintenanceCosts = maintenanceYears.map((y) => 
        calculatePresentValue(params.maintenanceCost, y - 1)
      );
      const totalMaintenancePV = maintenanceCosts.reduce((sum, cost) => sum + cost, 0);
      
      // Annual operational costs
      const annualGridCost = (currentConfig.gridNeededWithBattery * params.gridRate * 1000 * 365);
      
      // Present value calculation handled in cumulative calculation below
      
      // Cumulative PV costs
      const cumulativePVOperatingCost = Array.from({ length: year }, (_, i) => 
        calculatePresentValue(annualGridCost, i)
      ).reduce((sum, cost) => sum + cost, 0);
      
      const totalPVCost = initialInvestment + totalMaintenancePV + cumulativePVOperatingCost;
      
      // Total energy over the period
      const totalKwh = year * (currentConfig.dailyConsumption * 1000 * 365);
      
      // Levelized cost
      const levelizedCost = totalPVCost / totalKwh;
      
      // Calculate maintenance in nominal terms
      const maintenanceCount = params.batteryMaintenanceYears.filter(y => y <= year).length;
      const maintenanceCostNominal = maintenanceCount * params.maintenanceCost;
      
      return { 
        year, 
        value: levelizedCost,
        nominalValue: annualGridCost / (currentConfig.dailyConsumption * 1000 * 365),
        annualCost: annualGridCost,
        cumulativeCost: initialInvestment + maintenanceCostNominal + (annualGridCost * year)
      };
    }).filter(item => item !== null);
    
    // Generator Only
    const generatorCostsPerKwh = years.map(year => {
      if (year === 0) return null;
      
      const generatorHours = currentConfig.operatingHours;
      const annualGeneratorCost = (generatorHourlyCost * generatorHours * 365);
      
      // Present value calculation handled in cumulative calculation below
      
      // Cumulative PV costs
      const cumulativePVCost = Array.from({ length: year }, (_, index) => 
        calculatePresentValue(annualGeneratorCost, index)
      ).reduce((sum, cost) => sum + cost, 0);
      
      // Total energy over the period
      const totalKwh = year * (currentConfig.dailyConsumption * 1000 * 365);
      
      // Levelized cost
      const levelizedCost = cumulativePVCost / totalKwh;
      
      return { 
        year, 
        value: levelizedCost,
        nominalValue: annualGeneratorCost / (currentConfig.dailyConsumption * 1000 * 365),
        annualCost: annualGeneratorCost,
        cumulativeCost: annualGeneratorCost * year
      };
    }).filter(item => item !== null);
    
    return {
      gridOnly: gridOnlyCostsPerKwh,
      gridTied: gridTiedCostsPerKwh,
      battery: batteryCostsPerKwh,
      generator: generatorCostsPerKwh
    };
  }; */
  
  // Note: Cost data interfaces are defined in types/calculations.ts
  
  // We're already getting costs from useCalculations, so this is redundant
  // const costs = useMemo<CostData>(() => calculateYearlyCostsPerKwh(operatingScenario), [operatingScenario, params, config]);
  
  // We're now getting combinedChartData and cumulativeCostData from useCalculations
  
  // We're now getting NPV results from useCalculations
  
  // We're now getting lifecycleCostsTable from useCalculations
  
  // We're now getting breakeven points from useCalculations
  
  // We're now getting IRR calculations from useCalculations
  
  // We're now getting batteryLifespanYears and dailyPowerFlow from useCalculations
  
  // We're now getting energyMix from useCalculations
  
  // We're now getting carbonEmissions from useCalculations
  
  
  
  
  /**
   * Main render method
   */
  return (
    <motion.div 
      className="min-h-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <div className="py-10">
        <main>
          <motion.div 
            className="max-w-7xl mx-auto sm:px-6 lg:px-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            {/* Modern Header */}
            <motion.div 
              className="dashboard-header px-8 py-8 mb-8 rounded-2xl"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                <motion.div 
                  className="mb-4 md:mb-0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  <div className="flex items-center">
                    <motion.div
                      whileHover={{ rotate: 15, scale: 1.1 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <RiDashboardLine className="text-4xl mr-3 text-white drop-shadow-md" />
                    </motion.div>
                    <div>
                      <h1 className="text-3xl font-bold tracking-tight drop-shadow-sm">Solar Dashboard</h1>
                      <div className="h-1 w-16 bg-yellow-400 rounded mt-1"></div>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-white text-opacity-90 max-w-2xl font-light">
                    Comprehensive financial and environmental analysis for sustainable EV charging infrastructure
                  </p>
                  
                  <motion.div 
                    className="mt-4 glass-card-highlight px-4 py-2 inline-flex items-center"
                    whileHover={{ x: 5 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <FiInfo className="text-yellow-300 mr-2" />
                    <span className="text-sm font-medium">Using fixed grid rate of {params.gridRate.toFixed(3)} AED/kWh with no escalation</span>
                  </motion.div>
                </motion.div>
                
                <motion.div 
                  className="glass-card p-4 rounded-xl w-full md:w-auto"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  whileHover={{ y: -5, boxShadow: "0 10px 25px rgba(0,0,0,0.2)" }}
                >
                  <label htmlFor="operatingScenario" className="block text-sm font-semibold text-white mb-2 flex items-center">
                    <FiSettings className="mr-2" />
                    Operating Scenario
                  </label>
                  <select
                    id="operatingScenario"
                    value={operatingScenario}
                    onChange={(e) => setOperatingScenario(e.target.value as 'current' | 'expanded')}
                    className="block w-full pl-4 pr-10 py-2 text-base bg-white bg-opacity-90 border-0 focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg shadow-inner transition-all duration-200 hover:bg-opacity-100"
                  >
                    <option value="current">Current (6 Chargers, 14 Hours)</option>
                    <option value="expanded">Expanded (12 Chargers, 24 Hours)</option>
                  </select>
                </motion.div>
              </div>
            </motion.div>
            
            {/* Key metrics */}
            <motion.div 
              className="mt-6 mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <div className="flex items-center mb-4 px-2">
                <div className="h-8 w-1 bg-gradient-to-b from-blue-500 to-green-500 rounded-full mr-3"></div>
                <h2 className="text-lg font-semibold text-gray-800">Key Performance Indicators</h2>
                <div className="ml-auto">
                  <motion.div 
                    className="text-sm font-medium text-blue-600 flex items-center bg-blue-50 px-3 py-1 rounded-full"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>Based on {operatingScenario === 'current' ? '6 Chargers, 14 Hours' : '12 Chargers, 24 Hours'}</span>
                  </motion.div>
                </div>
              </div>
            
              <motion.div 
                className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
                variants={{
                  hidden: { opacity: 0 },
                  show: {
                    opacity: 1,
                    transition: {
                      staggerChildren: 0.1
                    }
                  }
                }}
                initial="hidden"
                animate="show"
              >
                <motion.div
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    show: { opacity: 1, y: 0, transition: { duration: 0.4 }}
                  }}
                >
                  <MetricCard 
                    title="Optimal Solution" 
                    value="Solar + Battery"
                    description="Lowest lifetime cost solution" 
                    color="green"
                  />
                </motion.div>

                <motion.div
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    show: { opacity: 1, y: 0, transition: { duration: 0.4 }}
                  }}
                >
                  <MetricCard 
                    title="25-Year Savings vs Grid" 
                    value={formatCurrency(npvResults[operatingScenario].gridOnlyVsBatterySavings)}
                    description="Net present value of savings" 
                    color="green"
                    trend={`${((npvResults[operatingScenario].gridOnlyVsBatterySavings / npvResults[operatingScenario].gridOnly) * 100).toFixed(0)}%`}
                    trendDirection="up"
                  />
                </motion.div>

                <motion.div
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    show: { opacity: 1, y: 0, transition: { duration: 0.4 }}
                  }}
                >
                  <MetricCard 
                    title="Payback Period" 
                    value={`${breakeven.batteryVsGrid.toFixed(1)} years`}
                    description={`Battery lifespan: ${batteryLifespanYears.toFixed(1)} years`}
                    color={breakeven.batteryVsGrid < batteryLifespanYears ? "green" : "yellow"}
                  />
                </motion.div>

                <motion.div
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    show: { opacity: 1, y: 0, transition: { duration: 0.4 }}
                  }}
                >
                  <MetricCard 
                    title="Levelized Cost (LCOE)" 
                    value={`${lifecycleCostsTable.find(r => r.year === 25)?.battery.toFixed(3)} AED/kWh`}
                    description={`vs grid nominal: ${params.gridRate.toFixed(3)} AED/kWh (${((1 - (lifecycleCostsTable.find(r => r.year === 25)?.battery || 0) / params.gridRate) * 100).toFixed(1)}% savings)`}
                    color="blue"
                    trend={`${((1 - (lifecycleCostsTable.find(r => r.year === 25)?.battery || 0) / params.gridRate) * 100).toFixed(1)}%`}
                    trendDirection="up"
                  />
                </motion.div>
              </motion.div>
            </motion.div>
            
            {/* Tab navigation */}
            <div className="modern-card bg-white rounded-xl shadow-lg overflow-hidden mb-8">
              <TabNav 
                tabs={[
                  { id: 'inputs', name: 'System Inputs' },
                  { id: 'comparison', name: 'Cost Comparison' },
                  { id: 'financial', name: 'Financial Analysis' },
                  { id: 'energy', name: 'Energy Flow' },
                  { id: 'emissions', name: 'Environmental Impact' },
                ]} 
                activeTab={activeTab} 
                onChange={setActiveTab} 
              />
              
              {/* Tab content */}
              <div className="p-6">
                {/* System Inputs Tab */}
                {activeTab === 'inputs' && (
                  <motion.div 
                    className="grid grid-cols-1 lg:grid-cols-3 gap-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="modern-card p-5">
                      <div className="flex items-center mb-4">
                        <FiDollarSign className="text-blue-500 mr-2 text-xl" />
                        <h2 className="text-xl font-semibold">System Costs</h2>
                      </div>
                      <NumericInput 
                        label="Solar Cost" 
                        name="solarCost" 
                        value={params.solarCost} 
                        onChange={updateParam}
                        prefix="AED"
                        format={formatCurrency}
                        description="Total cost of solar PV system"
                      />
                      <NumericInput 
                        label="Battery Cost" 
                        name="batteryCost" 
                        value={params.batteryCost} 
                        onChange={updateParam} 
                        prefix="AED"
                        format={formatCurrency}
                        description="Total cost of battery storage system"
                      />
                      <NumericInput 
                        label="Grid Connection Cost" 
                        name="connectionCost" 
                        value={params.connectionCost} 
                        onChange={updateParam}
                        prefix="AED"
                        format={formatCurrency}
                        description="Cost to connect to grid power"
                      />
                      <NumericInput 
                        label="Battery Maintenance Cost" 
                        name="maintenanceCost" 
                        value={params.maintenanceCost} 
                        onChange={updateParam}
                        prefix="AED"
                        format={formatCurrency}
                        description="Cost for battery maintenance at years 10 and 20"
                      />
                      <div className="mt-4 pt-2 border-t border-gray-200">
                        <p className="text-sm font-medium">Total System Cost: <span className="font-semibold">{formatCurrency(totalSystemCost)}</span></p>
                        <p className="text-sm font-medium mt-2">Battery Lifecycle: <span className="font-semibold">{params.batteryCycles} cycles ≈ {batteryLifespanYears.toFixed(1)} years</span></p>
                      </div>
                    </div>
                    
                    <div className="modern-card p-5">
                      <div className="flex items-center mb-4">
                        <FiActivity className="text-green-500 mr-2 text-xl" />
                        <h2 className="text-xl font-semibold">Operational Costs</h2>
                      </div>
                      <NumericInput 
                        label="Grid Rate" 
                        name="gridRate" 
                        value={params.gridRate} 
                        onChange={updateParam}
                        suffix="AED/kWh"
                        description="Cost of electricity from grid"
                      />
                      {params.showGridTiedAnalysis && (
                        <>
                          <NumericInput 
                            label="Feed-in Rate (DEWA)" 
                            name="feedInRate" 
                            value={params.feedInRate} 
                            onChange={() => {
                              // Use the context's updateFeedInRate function
                              updateFeedInRate(params.feedInRate);
                            }}
                            suffix="AED/kWh"
                            description="DEWA feed-in tariff (nominal)"
                          />
                          <NumericInput 
                            label="Effective Feed-in Rate" 
                            name="feedInEffectiveRate" 
                            value={params.feedInEffectiveRate} 
                            onChange={updateParam}
                            suffix="AED/kWh"
                            description="Effective feed-in rate after limitations"
                            tooltipContent={
                              <div>
                                <p className="font-medium">The effective feed-in rate accounts for regulatory restrictions:</p>
                                <ul className="list-disc list-inside mt-1 space-y-1">
                                  <li>In winter months (Dec-Feb), feed-in may be restricted due to grid congestion</li>
                                  <li>Feed-in is limited to prevent exceeding maximum load capacity</li>
                                  <li>Scheduled maintenance periods prevent grid export</li>
                                  <li>Calculation uses actual historical export profiles</li>
                                </ul>
                              </div>
                            }
                          />
                        </>
                      )}
                      {params.showGridTiedAnalysis && (
                        <InfoCard 
                          title="Feed-in Rate Adjustment Information"
                          content={
                            <div>
                              <p className="mb-2">
                                The effective feed-in rate ({(params.feedInEffectiveRate / params.feedInRate * 100).toFixed(0)}% of nominal) is dynamically calculated based on:
                              </p>
                              <div className="grid grid-cols-1 gap-2">
                                <div className="flex items-center">
                                  <div className="h-2 w-2 rounded-full bg-yellow-500 mr-2"></div>
                                  <span>Winter months (Dec-Feb): No feed-in allowed</span>
                                </div>
                                <div className="flex items-center">
                                  <div className="h-2 w-2 rounded-full bg-blue-500 mr-2"></div>
                                  <span>Max load limitations: 5% of exports disallowed</span>
                                </div>
                                <div className="flex items-center">
                                  <div className="h-2 w-2 rounded-full bg-green-500 mr-2"></div>
                                  <span>Maintenance downtime: 2% of time</span>
                                </div>
                              </div>
                              <div className="mt-2 text-xs text-gray-500">
                                Changing the nominal feed-in rate automatically updates the effective rate
                              </div>
                            </div>
                          }
                          color="blue"
                          icon={<FiInfo className="w-5 h-5" />}
                          dismissible={true}
                        />
                      )}
                      <NumericInput 
                        label="Generator Fuel Cost" 
                        name="generatorFuelCost" 
                        value={params.generatorFuelCost} 
                        onChange={updateParam}
                        suffix="AED/liter"
                        description="Cost of diesel fuel"
                      />
                      <NumericInput 
                        label="Generator Consumption" 
                        name="generatorConsumption" 
                        value={params.generatorConsumption} 
                        onChange={updateParam}
                        suffix="liters/hour"
                        description="Fuel consumption per generator"
                      />
                      <NumericInput 
                        label="Discount Rate" 
                        name="discountRate" 
                        value={params.discountRate} 
                        onChange={updateParam}
                        suffix=""
                        format={formatPercentage}
                        description="Discount rate for NPV calculations"
                      />
                    </div>
                    
                    <div className="modern-card p-5">
                      <div className="flex items-center mb-4">
                        <FiSettings className="text-blue-500 mr-2 text-xl" />
                        <h2 className="text-xl font-semibold">System Configuration</h2>
                      </div>
                      <NumericInput 
                        label="Daily Consumption" 
                        name="dailyConsumption" 
                        value={config[operatingScenario].dailyConsumption} 
                        onChange={(name, value) => updateConfig(operatingScenario, name, value)}
                        suffix="MWh"
                        description="Total energy consumed daily"
                      />
                      <NumericInput 
                        label="Solar Production" 
                        name="solarProduction" 
                        value={config[operatingScenario].solarProduction} 
                        onChange={(name, value) => updateConfig(operatingScenario, name, value)}
                        suffix="MWh/day"
                        description="Daily solar energy production"
                      />
                      <NumericInput 
                        label="Direct Solar Usage" 
                        name="directSolarUsage" 
                        value={config[operatingScenario].directSolarUsage} 
                        onChange={(name, value) => updateConfig(operatingScenario, name, value)}
                        suffix="MWh"
                        description="Solar energy used directly (not stored)"
                      />
                      <NumericInput 
                        label="Battery Usable Capacity" 
                        name="batteryUsable" 
                        value={config[operatingScenario].batteryUsable} 
                        onChange={(name, value) => updateConfig(operatingScenario, name, value)}
                        suffix="MWh"
                        description="Usable battery capacity (with DoD)"
                      />
                      <NumericInput 
                        label="Grid Needed (with Battery)" 
                        name="gridNeededWithBattery" 
                        value={config[operatingScenario].gridNeededWithBattery} 
                        onChange={(name, value) => updateConfig(operatingScenario, name, value)}
                        suffix="MWh"
                        description="Daily grid energy needed with battery"
                      />
                      <NumericInput 
                        label="Operating Hours" 
                        name="operatingHours" 
                        value={config[operatingScenario].operatingHours} 
                        onChange={(name, value) => updateConfig(operatingScenario, name, value)}
                        suffix="hours"
                        description="Daily charging station operation hours"
                      />
                      <NumericInput 
                        label="Solar Hours" 
                        name="solarHours" 
                        value={config[operatingScenario].solarHours} 
                        onChange={(name, value) => updateConfig(operatingScenario, name, value)}
                        suffix="hours"
                        description="Daily peak solar production hours"
                      />
                    </div>
                  </motion.div>
                )}
                
                {/* Cost Comparison Tab */}
                {activeTab === 'comparison' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                      <div className="chart-container">
                        <div className="flex items-center mb-4">
                          <RiLineChartLine className="text-blue-500 mr-2 text-xl" />
                          <h2 className="text-xl font-semibold">Levelized Cost of Energy (LCOE) Over System Lifespan</h2>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">LCOE represents the present value of all costs divided by total energy produced. This accounts for the time value of money using a {formatPercentage(params.discountRate)} discount rate.</p>
                        <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded mb-2">
                          <p><strong>Note:</strong> The grid rate is fixed at {params.gridRate.toFixed(3)} AED/kWh, but LCOE values appear to decrease over time because they include the time value of money. This represents the same grid price, just discounted to present value.</p>
                        </div>
                        <ResponsiveContainer width="100%" height={400}>
                          <LineChart data={combinedChartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="year" 
                              label={{ value: 'Years', position: 'insideBottomRight', offset: -10 }} 
                              domain={[1, params.batteryAnalysisYears]}
                              ticks={[1, 5, 10, 15, 20, 25]}
                            />
                            <YAxis 
                              label={{ value: 'LCOE (AED per kWh)', angle: -90, position: 'insideLeft' }} 
                              domain={[0, 0.5]}
                            />
                            <RechartsTooltip formatter={(value: any) => [`${Number(value).toFixed(3)} AED`, 'LCOE (includes time value of money)']} />
                            <Legend />
                            <Line type="monotone" dataKey="gridOnly" name="100% Grid" stroke="#4ECDC4" strokeWidth={2} />
                            {params.showGridTiedAnalysis && <Line type="monotone" dataKey="gridTied" name="Grid-Tied Solar" stroke="#FF9F1C" strokeWidth={2} />}
                            <Line type="monotone" dataKey="battery" name="Solar + Battery" stroke="#118AB2" strokeWidth={2} />
                            <Line type="monotone" dataKey="generator" name="Generator Only" stroke="#FF6B6B" strokeWidth={2} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                      
                      <div className="chart-container">
                        <div className="flex items-center mb-4">
                          <RiMoneyDollarCircleLine className="text-green-500 mr-2 text-xl" />
                          <h2 className="text-xl font-semibold">Cumulative Cost Comparison (Million AED)</h2>
                        </div>
                        <ResponsiveContainer width="100%" height={400}>
                          <LineChart data={cumulativeCostData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="year" 
                              label={{ value: 'Years', position: 'insideBottomRight', offset: -10 }} 
                              domain={[1, params.batteryAnalysisYears]}
                              ticks={[1, 5, 10, 15, 20, 25]}
                            />
                            <YAxis 
                              label={{ value: 'Million AED', angle: -90, position: 'insideLeft' }} 
                            />
                            <RechartsTooltip formatter={(value: any) => [`${Number(value).toFixed(2)} Million AED`, 'Cumulative Cost']} />
                            <Legend />
                            <Line type="monotone" dataKey="gridOnly" name="100% Grid" stroke="#4ECDC4" strokeWidth={2} />
                            {params.showGridTiedAnalysis && <Line type="monotone" dataKey="gridTied" name="Grid-Tied Solar" stroke="#FF9F1C" strokeWidth={2} />}
                            <Line type="monotone" dataKey="battery" name="Solar + Battery" stroke="#118AB2" strokeWidth={2} />
                            <Line type="monotone" dataKey="generator" name="Generator Only" stroke="#FF6B6B" strokeWidth={2} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg shadow mb-6 overflow-x-auto">
                      <h2 className="text-xl font-semibold mb-4">Levelized Cost of Energy (AED/kWh) at Different System Ages</h2>
                      <div className="text-sm text-gray-600 mb-4 bg-yellow-50 p-3 rounded">
                        <p className="font-medium">Understanding LCOE vs. Nominal Price:</p>
                        <p className="mt-1">• Nominal Grid Price: <span className="font-semibold">{params.gridRate.toFixed(3)} AED/kWh</span> (fixed price)</p>
                        <p className="mt-1">• LCOE Values: Decrease over time due to the discount rate of {formatPercentage(params.discountRate)}, which accounts for the time value of money</p>
                        <p className="mt-1">• This is why the Grid LCOE appears to decrease, even though the actual grid price remains constant</p>
                      </div>
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Year
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              100% Grid (LCOE)
                            </th>
                            {params.showGridTiedAnalysis && (
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Grid-Tied Solar (LCOE)
                              </th>
                            )}
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Solar + Battery (LCOE)
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Generator Only (LCOE)
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {lifecycleCostsTable.map((row) => (
                            <tr key={row.year} className={row.year % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.year}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.gridOnly.toFixed(3)}</td>
                              {params.showGridTiedAnalysis && (
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.gridTied.toFixed(3)}</td>
                              )}
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-semibold">{row.battery.toFixed(3)}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.generator.toFixed(3)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <div className="modern-card p-5">
                        <div className="flex items-center mb-3">
                          <FiGrid className="text-blue-500 mr-2" />
                          <h3 className="font-bold text-lg">100% Grid</h3>
                        </div>
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-xl font-semibold">{params.gridRate.toFixed(3)} AED/kWh</p>
                            <p className="text-xs text-gray-500">Nominal price (fixed)</p>
                          </div>
                          <div className="bg-blue-50 rounded-full p-1 text-blue-500" title="This is the actual grid price that remains constant">
                            <FiInfo className="h-5 w-5" />
                          </div>
                        </div>
                        <p className="mt-2 text-sm font-medium">25-year LCOE: <span className="font-semibold">{lifecycleCostsTable.find(r => r.year === 25)?.gridOnly.toFixed(3) || params.gridRate.toFixed(3)} AED/kWh</span></p>
                        <p className="text-xs text-gray-500">(Discounted at {formatPercentage(params.discountRate)})</p>
                        <p className="mt-2 text-sm font-medium">25-year NPV: <span className="font-semibold">{formatCurrency(npvResults[operatingScenario].gridOnly)}</span></p>
                      </div>
                      
                      {params.showGridTiedAnalysis && (
                        <div className="modern-card p-5">
                          <div className="flex items-center mb-3">
                            <FiSun className="text-yellow-500 mr-2" />
                            <h3 className="font-bold text-lg">Grid-Tied Solar</h3>
                          </div>
                          <p className="text-xl font-semibold">{lifecycleCostsTable.find(r => r.year === 25)?.gridTied.toFixed(3) || "0.000"} AED/kWh</p>
                          <p className="text-sm text-gray-500">25-year LCOE</p>
                          <div className="flex items-center">
                            <p className="text-sm text-green-600">Saves {((1 - (lifecycleCostsTable.find(r => r.year === 25)?.gridTied || 0) / params.gridRate) * 100).toFixed(1)}% vs grid nominal price</p>
                            <FiTrendingUp className="ml-1 text-green-600 text-sm" />
                          </div>
                          <p className="mt-2 text-sm font-medium">25-year NPV: <span className="font-semibold">{formatCurrency(npvResults[operatingScenario].gridTied)}</span></p>
                        </div>
                      )}
                      
                      <div className="modern-card p-5 border-l-4 border-green-500">
                        <div className="flex items-center mb-3">
                          <FiBattery className="text-green-500 mr-2" />
                          <h3 className="font-bold text-lg text-green-700">Solar + Battery</h3>
                        </div>
                        <p className="text-xl font-semibold text-green-700">{lifecycleCostsTable.find(r => r.year === 25)?.battery.toFixed(3) || "0.000"} AED/kWh</p>
                        <p className="text-sm text-gray-500">25-year LCOE</p>
                        <div className="flex items-center">
                          <p className="text-sm text-green-600">Saves {((1 - (lifecycleCostsTable.find(r => r.year === 25)?.battery || 0) / params.gridRate) * 100).toFixed(1)}% vs grid nominal price</p>
                          <FiTrendingUp className="ml-1 text-green-600 text-sm" />
                        </div>
                        <p className="mt-2 text-sm font-medium">25-year NPV: <span className="font-semibold">{formatCurrency(npvResults[operatingScenario].battery)}</span></p>
                      </div>
                      
                      <div className="modern-card p-5">
                        <div className="flex items-center mb-3">
                          <RiWindyLine className="text-red-500 mr-2" />
                          <h3 className="font-bold text-lg">Generator Only</h3>
                        </div>
                        <p className="text-xl font-semibold">{lifecycleCostsTable.find(r => r.year === 25)?.generator.toFixed(3) || "0.000"} AED/kWh</p>
                        <p className="text-sm text-gray-500">25-year LCOE</p>
                        <div className="flex items-center">
                          <p className="text-sm text-red-600">Costs {Math.max(0, ((lifecycleCostsTable.find(r => r.year === 25)?.generator || 0) / params.gridRate - 1) * 100).toFixed(1)}% more than grid nominal price</p>
                          <FiTrendingDown className="ml-1 text-red-600 text-sm" />
                        </div>
                        <p className="mt-2 text-sm font-medium">25-year NPV: <span className="font-semibold">{formatCurrency(npvResults[operatingScenario].generator)}</span></p>
                      </div>
                    </div>
                  </motion.div>
                )}
                
                {/* Financial Analysis Tab */}
                {activeTab === 'financial' && (
                  <div>
                    <div className="mb-6">
                      <SummaryTable 
                        scenario={operatingScenario} 
                        showAllOptions={true}
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                      <div className="bg-white p-4 rounded-lg shadow">
                        <h2 className="text-xl font-semibold mb-4">Investment Performance</h2>
                        <div className="grid grid-cols-1 gap-4">
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-bold mb-2">Solar + Battery vs Grid</h3>
                            <dl className="grid grid-cols-2 gap-2">
                              <dt className="text-sm text-gray-600">Initial Investment:</dt>
                              <dd className="text-sm font-medium">{formatCurrency(totalSystemCost)}</dd>
                              
                              <dt className="text-sm text-gray-600">Payback Period:</dt>
                              <dd className="text-sm font-medium">{breakeven.batteryVsGrid.toFixed(1)} years</dd>
                              
                              <dt className="text-sm text-gray-600">IRR:</dt>
                              <dd className="text-sm font-medium">{(irr.batteryVsGrid * 100).toFixed(1)}%</dd>
                              
                              <dt className="text-sm text-gray-600">NPV of Savings:</dt>
                              <dd className="text-sm font-medium">{formatCurrency(npvResults[operatingScenario].gridOnlyVsBatterySavings)}</dd>
                              
                              <dt className="text-sm text-gray-600">ROI:</dt>
                              <dd className="text-sm font-medium">{(npvResults[operatingScenario].gridOnlyVsBatterySavings / (totalSystemCost + params.connectionCost) * 100).toFixed(1)}%</dd>
                            </dl>
                          </div>
                          
                          {params.showGridTiedAnalysis && (
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <h3 className="font-bold mb-2">Solar + Battery vs Grid-Tied</h3>
                              <dl className="grid grid-cols-2 gap-2">
                                <dt className="text-sm text-gray-600">Additional Investment:</dt>
                                <dd className="text-sm font-medium">{formatCurrency(params.batteryCost)}</dd>
                                
                                <dt className="text-sm text-gray-600">Payback Period:</dt>
                                <dd className="text-sm font-medium">{breakeven.batteryVsGridTied.toFixed(1)} years</dd>
                                
                                <dt className="text-sm text-gray-600">NPV of Savings:</dt>
                                <dd className="text-sm font-medium">{formatCurrency(npvResults[operatingScenario].gridTiedVsBatterySavings)}</dd>
                                
                                <dt className="text-sm text-gray-600">ROI:</dt>
                                <dd className="text-sm font-medium">{(npvResults[operatingScenario].gridTiedVsBatterySavings / params.batteryCost * 100).toFixed(1)}%</dd>
                              </dl>
                            </div>
                          )}
                          
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-bold mb-2">Battery Lifecycle Economics</h3>
                            <dl className="grid grid-cols-2 gap-2">
                              <dt className="text-sm text-gray-600">Battery Cost:</dt>
                              <dd className="text-sm font-medium">{formatCurrency(params.batteryCost)}</dd>
                              
                              <dt className="text-sm text-gray-600">Cycle Life:</dt>
                              <dd className="text-sm font-medium">{params.batteryCycles} cycles</dd>
                              
                              <dt className="text-sm text-gray-600">Expected Lifespan:</dt>
                              <dd className="text-sm font-medium">{batteryLifespanYears.toFixed(1)} years</dd>
                              
                              <dt className="text-sm text-gray-600">Cost per Cycle:</dt>
                              <dd className="text-sm font-medium">{formatCurrency(params.batteryCost / params.batteryCycles)}</dd>
                              
                              <dt className="text-sm text-gray-600">Cost per kWh stored:</dt>
                              <dd className="text-sm font-medium">{formatCurrency(params.batteryCost / (params.batteryCycles * config[operatingScenario].batteryUsable * 1000))}</dd>
                              
                              <dt className="text-sm text-gray-600">Residual Value:</dt>
                              <dd className="text-sm font-medium">{formatCurrency(params.batteryCost * params.batteryResidualValue)} ({formatPercentage(params.batteryResidualValue)})</dd>
                            </dl>
                          </div>
                          
                          <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-green-500">
                            <h3 className="font-bold mb-2">Levelized Cost of Energy (LCOE)</h3>
                            <div className="bg-blue-50 p-2 rounded mb-3 text-xs">
                              <p><strong>Note:</strong> LCOE represents the present value of all costs divided by total energy produced. Values include the time value of money using a {formatPercentage(params.discountRate)} discount rate.</p>
                            </div>
                            <dl className="grid grid-cols-2 gap-2">
                              <dt className="text-sm text-gray-600">Battery LCOE:</dt>
                              <dd className="text-sm font-medium">{npvResults[operatingScenario].batteryLCOE.toFixed(3)} AED/kWh</dd>
                              
                              <dt className="text-sm text-gray-600">Grid LCOE:</dt>
                              <dd className="text-sm font-medium">{npvResults[operatingScenario].gridOnlyLCOE.toFixed(3)} AED/kWh</dd>
                              
                              <dt className="text-sm text-gray-600">LCOE Savings:</dt>
                              <dd className="text-sm font-medium text-green-600">{((npvResults[operatingScenario].gridOnlyLCOE - npvResults[operatingScenario].batteryLCOE) / npvResults[operatingScenario].gridOnlyLCOE * 100).toFixed(1)}%</dd>
                              
                              <dt className="text-sm text-gray-600">Grid Nominal Price:</dt>
                              <dd className="text-sm font-medium">{params.gridRate.toFixed(3)} AED/kWh (fixed)</dd>
                              
                              <dt className="text-sm text-gray-600">Terminal Value:</dt>
                              <dd className="text-sm font-medium">{formatCurrency(params.solarCost * 0.2)}</dd>
                              
                              <dt className="text-sm text-gray-600">Grid Services Revenue:</dt>
                              <dd className="text-sm font-medium">{formatCurrency(params.frequencyRegulationRevenue + params.demandResponseRevenue)}/year</dd>
                            </dl>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white p-4 rounded-lg shadow">
                        <h2 className="text-xl font-semibold mb-4">Sensitivity Analysis</h2>
                        <div className="grid grid-cols-1 gap-4">
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-bold mb-2">Grid Rate Impact</h3>
                            <p className="text-sm text-gray-600 mb-2">
                              Higher grid electricity rates improve solar and battery economics. Every 0.05 AED/kWh increase in grid rates reduces payback period by approximately {(0.05 * 365 * config[operatingScenario].dailyConsumption * 1000 / (totalSystemCost + params.connectionCost) * breakeven.batteryVsGrid).toFixed(1)} years.
                            </p>
                            <p className="text-sm text-gray-600">
                              Current grid rate: <span className="font-medium">{params.gridRate.toFixed(3)} AED/kWh</span>
                            </p>
                          </div>
                          
                          {params.showGridTiedAnalysis && (
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <h3 className="font-bold mb-2">Feed-in Tariff Impact</h3>
                              <p className="text-sm text-gray-600 mb-2">
                                The feed-in tariff primarily affects grid-tied solar economics. Current DEWA rate of {params.feedInRate.toFixed(3)} AED/kWh with limitations (effective rate: {params.feedInEffectiveRate.toFixed(3)} AED/kWh) makes battery storage more valuable.
                              </p>
                              <p className="text-sm text-gray-600">
                                Feed-in to grid purchase ratio: <span className="font-medium">{(params.feedInEffectiveRate / params.gridRate * 100).toFixed(1)}%</span>
                              </p>
                            </div>
                          )}
                          
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-bold mb-2">Battery Cost Impact</h3>
                            <p className="text-sm text-gray-600 mb-2">
                              Each 1 million AED reduction in battery cost improves payback by approximately {(1000000 / (config[operatingScenario].dailyConsumption * params.gridRate * 1000 * 365 - config[operatingScenario].gridNeededWithBattery * params.gridRate * 1000 * 365)).toFixed(1)} years.
                            </p>
                            <p className="text-sm text-gray-600">
                              Current battery cost: <span className="font-medium">{formatCurrency(params.batteryCost)}</span>
                            </p>
                          </div>
                          
                          <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-blue-500">
                            <h3 className="font-bold mb-2">IFRS Accounting Approach</h3>
                            <p className="text-sm text-gray-600 mb-2">
                              The financial analysis uses component accounting under IAS 16, with battery depreciation based on cycles ({params.batteryCycles}) rather than calendar years. Maintenance costs at years {params.batteryMaintenanceYears.join(' & ')} are capitalized as major overhauls.
                            </p>
                            <p className="text-sm text-gray-600">
                              Battery residual value: <span className="font-medium">{formatPercentage(params.batteryResidualValue)}</span> of initial cost
                            </p>
                            <p className="text-sm text-gray-600">
                              Solar analysis period: <span className="font-medium">{params.solarAnalysisYears} years</span> with terminal value
                            </p>
                          </div>
                          
                          <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-green-500">
                            <h3 className="font-bold mb-2">Grid Services Revenue</h3>
                            <p className="text-sm text-gray-600 mb-2">
                              Analysis includes fixed grid electricity price and additional revenue streams from frequency regulation and demand response services.
                            </p>
                            <div className="flex items-center bg-yellow-50 p-2 rounded mb-2">
                              <div className="text-yellow-600 mr-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <p className="text-xs text-gray-800 font-medium">
                                Grid rate is fixed at <span className="font-bold">{params.gridRate.toFixed(3)} AED/kWh</span> with no escalation. This provides a clear comparison baseline for all energy options.
                              </p>
                            </div>
                            <p className="text-sm text-gray-600">
                              Annual grid services revenue: <span className="font-medium">{formatCurrency(params.frequencyRegulationRevenue + params.demandResponseRevenue)}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg shadow">
                      <h2 className="text-xl font-semibold mb-4">IFRS-Optimized Long-term Cost Analysis</h2>
                      <div className="bg-blue-50 p-3 rounded mb-4 flex items-start">
                        <div className="text-blue-600 mr-2 flex-shrink-0 mt-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="text-sm text-gray-700">
                          <p className="font-medium mb-1">Financial Analysis Method</p>
                          <ul className="list-disc list-inside text-xs space-y-1">
                            <li>Risk-adjusted discount rate: <span className="font-semibold">{formatPercentage(params.discountRate)}</span></li>
                            <li>Uses component accounting under IAS 16</li>
                            <li>Extended solar analysis to {params.solarAnalysisYears} years with terminal value</li>
                            <li>Fixed grid rate of <span className="font-semibold">{params.gridRate.toFixed(3)} AED/kWh</span> with no escalation</li>
                          </ul>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Cost Element
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                100% Grid
                              </th>
                              {params.showGridTiedAnalysis && (
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Grid-Tied Solar
                                </th>
                              )}
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Solar + Battery
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Generator Only
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            <tr>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                Initial Investment
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(0)}
                              </td>
                              {params.showGridTiedAnalysis && (
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {formatCurrency(params.solarCost + params.connectionCost)}
                                </td>
                              )}
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(totalSystemCost + params.connectionCost)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(0)}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                Annual Operating Cost
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(config[operatingScenario].dailyConsumption * params.gridRate * 1000 * 365)}
                              </td>
                              {params.showGridTiedAnalysis && (
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {formatCurrency((config[operatingScenario].gridNeeded * params.gridRate * 1000 * 365) - (config[operatingScenario].excessSolar * params.feedInEffectiveRate * 1000 * 365))}
                                </td>
                              )}
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(config[operatingScenario].gridNeededWithBattery * params.gridRate * 1000 * 365)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(generatorHourlyCost * config[operatingScenario].operatingHours * 365)}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                Capital Improvements
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(0)}
                              </td>
                              {params.showGridTiedAnalysis && (
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {formatCurrency(0)}
                                </td>
                              )}
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(params.maintenanceCost)} at years 10 & 20
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(0)}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                Terminal/Residual Value
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(0)}
                              </td>
                              {params.showGridTiedAnalysis && (
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {formatCurrency(params.solarCost * 0.2)}
                                </td>
                              )}
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(params.solarCost * 0.2 + params.batteryCost * params.batteryResidualValue)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(0)}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 bg-gray-50">
                                Grid Services Revenue (Annual)
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 bg-gray-50">
                                {formatCurrency(0)}
                              </td>
                              {params.showGridTiedAnalysis && (
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 bg-gray-50">
                                  {formatCurrency(0)}
                                </td>
                              )}
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold bg-gray-50">
                                {formatCurrency(params.frequencyRegulationRevenue + params.demandResponseRevenue)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 bg-gray-50">
                                {formatCurrency(0)}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                25-Year NPV
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(npvResults[operatingScenario].gridOnly)}
                              </td>
                              {params.showGridTiedAnalysis && (
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {formatCurrency(npvResults[operatingScenario].gridTied)}
                                </td>
                              )}
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-semibold">
                                {formatCurrency(npvResults[operatingScenario].battery)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(npvResults[operatingScenario].generator)}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                Savings vs Grid
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                -
                              </td>
                              {params.showGridTiedAnalysis && (
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                                  {formatCurrency(npvResults[operatingScenario].gridOnly - npvResults[operatingScenario].gridTied)}
                                </td>
                              )}
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold">
                                {formatCurrency(npvResults[operatingScenario].gridOnlyVsBatterySavings)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                                {formatCurrency(npvResults[operatingScenario].gridOnly - npvResults[operatingScenario].generator)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Energy Flow Tab */}
                {activeTab === 'energy' && (
                  <div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                      <div className="bg-white p-4 rounded-lg shadow">
                        <h2 className="text-xl font-semibold mb-4">Daily Power Flow (kW)</h2>
                        <ResponsiveContainer width="100%" height={400}>
                          <AreaChart data={dailyPowerFlow}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="hour" />
                            <YAxis label={{ value: 'Power (kW)', angle: -90, position: 'insideLeft' }} />
                            <RechartsTooltip />
                            <Legend />
                            <Area type="monotone" dataKey="solar" stackId="1" name="Solar Direct" fill="#FFD166" stroke="#FFD166" />
                            <Area type="monotone" dataKey="battery" stackId="1" name="Battery" fill="#06D6A0" stroke="#06D6A0" />
                            <Area type="monotone" dataKey="grid" stackId="1" name="Grid" fill="#118AB2" stroke="#118AB2" />
                            <Line type="monotone" dataKey="demand" name="Charger Demand" stroke="#EF476F" strokeWidth={2} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                      
                      <div className="bg-white p-4 rounded-lg shadow">
                        <h2 className="text-xl font-semibold mb-4">Energy Source Mix (with Battery)</h2>
                        <ResponsiveContainer width="100%" height={400}>
                          <PieChart>
                            <Pie
                              data={energyMix}
                              cx="50%"
                              cy="50%"
                              labelLine={true}
                              outerRadius={150}
                              fill="#8884d8"
                              dataKey="value"
                              nameKey="name"
                              label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                              {energyMix.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <RechartsTooltip formatter={(value: any) => [`${Number(value).toFixed(2)} MWh`, 'Energy']} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="bg-white p-4 rounded-lg shadow">
                        <h2 className="text-xl font-semibold mb-4">Energy Balance</h2>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Energy Component
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Daily (MWh)
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Annual (GWh)
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Percentage
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  Total Consumption
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {config[operatingScenario].dailyConsumption.toFixed(2)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(config[operatingScenario].dailyConsumption * 365 / 1000).toFixed(2)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  100%
                                </td>
                              </tr>
                              <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  Solar Production
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {config[operatingScenario].solarProduction.toFixed(2)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(config[operatingScenario].solarProduction * 365 / 1000).toFixed(2)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(config[operatingScenario].solarProduction / config[operatingScenario].dailyConsumption * 100).toFixed(1)}%
                                </td>
                              </tr>
                              <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  Direct Solar Usage
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {config[operatingScenario].directSolarUsage.toFixed(2)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(config[operatingScenario].directSolarUsage * 365 / 1000).toFixed(2)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(config[operatingScenario].directSolarUsage / config[operatingScenario].dailyConsumption * 100).toFixed(1)}%
                                </td>
                              </tr>
                              <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  Battery Storage
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {config[operatingScenario].batteryUsable.toFixed(2)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(config[operatingScenario].batteryUsable * 365 / 1000).toFixed(2)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(config[operatingScenario].batteryUsable / config[operatingScenario].dailyConsumption * 100).toFixed(1)}%
                                </td>
                              </tr>
                              <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  Grid Energy
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {config[operatingScenario].gridNeededWithBattery.toFixed(2)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(config[operatingScenario].gridNeededWithBattery * 365 / 1000).toFixed(2)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(config[operatingScenario].gridNeededWithBattery / config[operatingScenario].dailyConsumption * 100).toFixed(1)}%
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      
                      <div className="bg-white p-4 rounded-lg shadow">
                        <h2 className="text-xl font-semibold mb-4">System Performance</h2>
                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                          <h3 className="font-bold mb-2">Solar System</h3>
                          <dl className="grid grid-cols-2 gap-2">
                            <dt className="text-sm text-gray-600">Peak Capacity:</dt>
                            <dd className="text-sm font-medium">{config[operatingScenario].solarProduction / config[operatingScenario].solarHours * 1000} kW</dd>
                            
                            <dt className="text-sm text-gray-600">Daily Production:</dt>
                            <dd className="text-sm font-medium">{config[operatingScenario].solarProduction.toFixed(2)} MWh</dd>
                            
                            <dt className="text-sm text-gray-600">Annual Production:</dt>
                            <dd className="text-sm font-medium">{(config[operatingScenario].solarProduction * 365 / 1000).toFixed(2)} GWh</dd>
                            
                            <dt className="text-sm text-gray-600">Solar Fraction:</dt>
                            <dd className="text-sm font-medium">{((config[operatingScenario].directSolarUsage + config[operatingScenario].batteryUsable) / config[operatingScenario].dailyConsumption * 100).toFixed(1)}%</dd>
                          </dl>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                          <h3 className="font-bold mb-2">Battery System</h3>
                          <dl className="grid grid-cols-2 gap-2">
                            <dt className="text-sm text-gray-600">Capacity:</dt>
                            <dd className="text-sm font-medium">{config[operatingScenario].batteryUsable.toFixed(2)} MWh</dd>
                            
                            <dt className="text-sm text-gray-600">Daily Cycles:</dt>
                            <dd className="text-sm font-medium">1</dd>
                            
                            <dt className="text-sm text-gray-600">Lifetime Cycles:</dt>
                            <dd className="text-sm font-medium">{params.batteryCycles}</dd>
                            
                            <dt className="text-sm text-gray-600">Expected Lifespan:</dt>
                            <dd className="text-sm font-medium">{batteryLifespanYears.toFixed(1)} years</dd>
                          </dl>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="font-bold mb-2">Grid Integration</h3>
                          <dl className="grid grid-cols-2 gap-2">
                            <dt className="text-sm text-gray-600">Grid Dependency:</dt>
                            <dd className="text-sm font-medium">{(config[operatingScenario].gridNeededWithBattery / config[operatingScenario].dailyConsumption * 100).toFixed(1)}%</dd>
                            
                            <dt className="text-sm text-gray-600">Self-Consumption:</dt>
                            <dd className="text-sm font-medium">{((config[operatingScenario].directSolarUsage + config[operatingScenario].batteryUsable) / config[operatingScenario].solarProduction * 100).toFixed(1)}%</dd>
                            
                            <dt className="text-sm text-gray-600">Grid Export:</dt>
                            <dd className="text-sm font-medium">{(Math.max(0, config[operatingScenario].solarProduction - config[operatingScenario].directSolarUsage - config[operatingScenario].batteryUsable)).toFixed(2)} MWh/day</dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Environmental Impact Tab */}
                {activeTab === 'emissions' && (
                  <div>
                    {/* Key metrics for emissions */}
                    <div className="mb-6">
                      <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        <MetricCard 
                          title="Grid Carbon Emission Factor" 
                          value={`${carbonEmissions.gridEmissionFactor} kg CO₂/kWh`}
                          description="UAE electricity grid carbon intensity" 
                          color="blue"
                        />
                        <MetricCard 
                          title="Carbon Credit Cost" 
                          value={`${carbonEmissions.carbonCreditCost} AED/MT CO₂`}
                          description="Cost to offset one tonne of CO₂" 
                          color="green"
                        />
                        <MetricCard 
                          title="Annual Carbon Savings" 
                          value={`${(carbonEmissions.batteryVsGridSavings / 1000).toFixed(1)} MT CO₂`}
                          description={`${((carbonEmissions.gridOnly - carbonEmissions.battery) / carbonEmissions.gridOnly * 100).toFixed(1)}% reduction vs grid`}
                          color="green"
                        />
                        <MetricCard 
                          title="Annual Offset Cost Savings" 
                          value={formatCurrency(carbonEmissions.batteryVsGridOffsetSavings)}
                          description="Financial value of emissions reduction" 
                          color="green"
                        />
                      </dl>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                      <div className="bg-white p-4 rounded-lg shadow">
                        <h2 className="text-xl font-semibold mb-4">Annual Carbon Emissions (tonnes CO₂e)</h2>
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart data={[
                            { name: '100% Grid', emissions: carbonEmissions.gridOnly / 1000 },
                            ...(params.showGridTiedAnalysis ? [{ name: 'Grid-Tied Solar', emissions: carbonEmissions.gridTied / 1000 }] : []),
                            { name: 'Solar + Battery', emissions: carbonEmissions.battery / 1000 },
                            { name: 'Generator', emissions: carbonEmissions.generator / 1000 }
                          ]}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis label={{ value: 'Tonnes CO₂e', angle: -90, position: 'insideLeft' }} />
                            <RechartsTooltip formatter={(value: any) => [`${Number(value).toFixed(1)} tonnes CO₂e`, 'Annual Emissions']} />
                            <Bar dataKey="emissions" fill="#06D6A0" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      
                      <div className="bg-white p-4 rounded-lg shadow">
                        <h2 className="text-xl font-semibold mb-4">Annual Carbon Offset Costs</h2>
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart data={[
                            { name: '100% Grid', cost: carbonEmissions.gridOnlyOffsetCost },
                            ...(params.showGridTiedAnalysis ? [{ name: 'Grid-Tied Solar', cost: carbonEmissions.gridTiedOffsetCost }] : []),
                            { name: 'Solar + Battery', cost: carbonEmissions.batteryOffsetCost },
                            { name: 'Generator', cost: carbonEmissions.generatorOffsetCost }
                          ]}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis label={{ value: 'AED per Year', angle: -90, position: 'insideLeft' }} />
                            <RechartsTooltip formatter={(value: any) => [formatCurrency(Number(value)), 'Annual Offset Cost']} />
                            <Bar dataKey="cost" fill="#118AB2" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                      <div className="bg-white p-4 rounded-lg shadow">
                        <h2 className="text-xl font-semibold mb-4">25-Year Cumulative Emissions</h2>
                        <ResponsiveContainer width="100%" height={400}>
                          <AreaChart data={Array.from({ length: 26 }, (_, i) => ({
                            year: i,
                            gridOnly: i * carbonEmissions.gridOnly / 1000,
                            ...(params.showGridTiedAnalysis ? { gridTied: i * carbonEmissions.gridTied / 1000 } : {}),
                            battery: i * carbonEmissions.battery / 1000,
                            generator: i * carbonEmissions.generator / 1000
                          }))}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="year" label={{ value: 'Years', position: 'insideBottomRight', offset: -10 }} />
                            <YAxis label={{ value: 'Tonnes CO₂e', angle: -90, position: 'insideLeft' }} />
                            <RechartsTooltip formatter={(value: any) => [`${Number(value).toFixed(1)} tonnes CO₂e`, 'Cumulative Emissions']} />
                            <Legend />
                            <Area type="monotone" dataKey="gridOnly" name="100% Grid" fill="#4ECDC4" stroke="#4ECDC4" />
                            {params.showGridTiedAnalysis && <Area type="monotone" dataKey="gridTied" name="Grid-Tied Solar" fill="#FF9F1C" stroke="#FF9F1C" />}
                            <Area type="monotone" dataKey="battery" name="Solar + Battery" fill="#06D6A0" stroke="#06D6A0" />
                            <Area type="monotone" dataKey="generator" name="Generator Only" fill="#FF6B6B" stroke="#FF6B6B" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                      
                      <div className="bg-white p-4 rounded-lg shadow">
                        <h2 className="text-xl font-semibold mb-4">25-Year Cumulative Offset Costs</h2>
                        <ResponsiveContainer width="100%" height={400}>
                          <AreaChart data={Array.from({ length: 26 }, (_, i) => ({
                            year: i,
                            gridOnly: i * carbonEmissions.gridOnlyOffsetCost,
                            ...(params.showGridTiedAnalysis ? { gridTied: i * carbonEmissions.gridTiedOffsetCost } : {}),
                            battery: i * carbonEmissions.batteryOffsetCost,
                            generator: i * carbonEmissions.generatorOffsetCost
                          }))}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="year" label={{ value: 'Years', position: 'insideBottomRight', offset: -10 }} />
                            <YAxis label={{ value: 'Offset Cost (AED)', angle: -90, position: 'insideLeft' }} />
                            <RechartsTooltip formatter={(value: any) => [formatCurrency(Number(value)), 'Cumulative Offset Cost']} />
                            <Legend />
                            <Area type="monotone" dataKey="gridOnly" name="100% Grid" fill="#4ECDC4" stroke="#4ECDC4" />
                            {params.showGridTiedAnalysis && <Area type="monotone" dataKey="gridTied" name="Grid-Tied Solar" fill="#FF9F1C" stroke="#FF9F1C" />}
                            <Area type="monotone" dataKey="battery" name="Solar + Battery" fill="#06D6A0" stroke="#06D6A0" />
                            <Area type="monotone" dataKey="generator" name="Generator Only" fill="#FF6B6B" stroke="#FF6B6B" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                      <div className="bg-white p-4 rounded-lg shadow">
                        <h2 className="text-xl font-semibold mb-4">Carbon Emissions Analysis</h2>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Metric
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  100% Grid
                                </th>
                                {params.showGridTiedAnalysis && (
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Grid-Tied Solar
                                  </th>
                                )}
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Solar + Battery
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Generator
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  Annual Emissions (tonnes CO₂e)
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(carbonEmissions.gridOnly / 1000).toFixed(1)}
                                </td>
                                {params.showGridTiedAnalysis && (
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {(carbonEmissions.gridTied / 1000).toFixed(1)}
                                  </td>
                                )}
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-semibold">
                                  {(carbonEmissions.battery / 1000).toFixed(1)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(carbonEmissions.generator / 1000).toFixed(1)}
                                </td>
                              </tr>
                              <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  Annual Offset Cost (AED)
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {formatCurrency(carbonEmissions.gridOnlyOffsetCost)}
                                </td>
                                {params.showGridTiedAnalysis && (
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {formatCurrency(carbonEmissions.gridTiedOffsetCost)}
                                  </td>
                                )}
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-semibold">
                                  {formatCurrency(carbonEmissions.batteryOffsetCost)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {formatCurrency(carbonEmissions.generatorOffsetCost)}
                                </td>
                              </tr>
                              <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  25-Year Emissions (tonnes CO₂e)
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(carbonEmissions.gridOnly25Year / 1000).toFixed(1)}
                                </td>
                                {params.showGridTiedAnalysis && (
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {(carbonEmissions.gridTied25Year / 1000).toFixed(1)}
                                  </td>
                                )}
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-semibold">
                                  {(carbonEmissions.battery25Year / 1000).toFixed(1)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(carbonEmissions.generator25Year / 1000).toFixed(1)}
                                </td>
                              </tr>
                              <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  25-Year Offset Cost (AED)
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {formatCurrency(carbonEmissions.gridOnlyOffsetCost25Year)}
                                </td>
                                {params.showGridTiedAnalysis && (
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {formatCurrency(carbonEmissions.gridTiedOffsetCost25Year)}
                                  </td>
                                )}
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-semibold">
                                  {formatCurrency(carbonEmissions.batteryOffsetCost25Year)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {formatCurrency(carbonEmissions.generatorOffsetCost25Year)}
                                </td>
                              </tr>
                              <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  25-Year Emissions Savings (tonnes CO₂e)
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  -
                                </td>
                                {params.showGridTiedAnalysis && (
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                                    {((carbonEmissions.gridOnly25Year - carbonEmissions.gridTied25Year) / 1000).toFixed(1)}
                                  </td>
                                )}
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold">
                                  {(carbonEmissions.batteryVsGridSavings25Year / 1000).toFixed(1)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                                  {((carbonEmissions.gridOnly25Year - carbonEmissions.generator25Year) / 1000).toFixed(1)}
                                </td>
                              </tr>
                              <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  25-Year Offset Cost Savings (AED)
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  -
                                </td>
                                {params.showGridTiedAnalysis && (
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                                    {formatCurrency(carbonEmissions.gridOnlyOffsetCost25Year - carbonEmissions.gridTiedOffsetCost25Year)}
                                  </td>
                                )}
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold">
                                  {formatCurrency(carbonEmissions.batteryVsGridOffsetSavings25Year)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                                  {formatCurrency(carbonEmissions.gridOnlyOffsetCost25Year - carbonEmissions.generatorOffsetCost25Year)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      
                      <div className="bg-white p-4 rounded-lg shadow">
                        <h2 className="text-xl font-semibold mb-4">Environmental Benefits</h2>
                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                          <h3 className="font-bold mb-2">Carbon Footprint Reduction</h3>
                          <p className="text-sm text-gray-600 mb-2">
                            The Solar + Battery solution reduces carbon emissions by <span className="font-semibold text-green-600">{((carbonEmissions.gridOnly - carbonEmissions.battery) / carbonEmissions.gridOnly * 100).toFixed(1)}%</span> compared to grid power.
                          </p>
                          <p className="text-sm text-gray-600">
                            Over 25 years, this represents a savings of <span className="font-semibold text-green-600">{(carbonEmissions.batteryVsGridSavings25Year / 1000).toFixed(1)} tonnes CO₂e</span>, equivalent to:
                          </p>
                          <ul className="list-disc list-inside mt-2 text-sm text-gray-600">
                            <li>{((carbonEmissions.batteryVsGridSavings25Year / 1000) / 2.3).toFixed(0)} passenger vehicles driven for one year</li>
                            <li>{((carbonEmissions.batteryVsGridSavings25Year / 1000) * 0.12).toFixed(0)} homes&apos; electricity use for one year</li>
                            <li>{((carbonEmissions.batteryVsGridSavings25Year / 1000) * 16.5).toFixed(0)} acres of forest preserved for one year</li>
                          </ul>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                          <h3 className="font-bold mb-2">Carbon Offset Economics</h3>
                          <p className="text-sm text-gray-600 mb-2">
                            Based on the carbon credit cost of <span className="font-semibold">{carbonEmissions.carbonCreditCost} AED per tonne CO₂</span>, the Solar + Battery solution provides carbon offset savings of:
                          </p>
                          <ul className="list-none mt-2 text-sm text-gray-600">
                            <li className="flex justify-between py-1 border-b">
                              <span>Annual savings:</span>
                              <span className="font-semibold text-green-600">{formatCurrency(carbonEmissions.batteryVsGridOffsetSavings)}</span>
                            </li>
                            <li className="flex justify-between py-1 border-b">
                              <span>5-year savings:</span>
                              <span className="font-semibold text-green-600">{formatCurrency(carbonEmissions.batteryVsGridOffsetSavings * 5)}</span>
                            </li>
                            <li className="flex justify-between py-1 border-b">
                              <span>10-year savings:</span>
                              <span className="font-semibold text-green-600">{formatCurrency(carbonEmissions.batteryVsGridOffsetSavings * 10)}</span>
                            </li>
                            <li className="flex justify-between py-1">
                              <span>25-year savings:</span>
                              <span className="font-semibold text-green-600">{formatCurrency(carbonEmissions.batteryVsGridOffsetSavings25Year)}</span>
                            </li>
                          </ul>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="font-bold mb-2">Sustainable Development Goals</h3>
                          <p className="text-sm text-gray-600 mb-2">
                            This project contributes to several UN Sustainable Development Goals:
                          </p>
                          <ul className="list-disc list-inside mt-2 text-sm text-gray-600">
                            <li><span className="font-semibold">Goal 7:</span> Affordable and Clean Energy</li>
                            <li><span className="font-semibold">Goal 9:</span> Industry, Innovation and Infrastructure</li>
                            <li><span className="font-semibold">Goal 11:</span> Sustainable Cities and Communities</li>
                            <li><span className="font-semibold">Goal 13:</span> Climate Action</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </main>
      </div>
    </motion.div>
  );
};

// Export the wrapper component as the default export
export default DynamicDashboardWrapper;